import { createApi } from "@reduxjs/toolkit/query/react";
import { apiRequestBaseQuery } from "../../services/rtkBaseQuery";
import { getUserDetails } from "../../services/apiRequest";
import { requestType } from "../../services/requests";

// =============================================================================
// ROYAL MOMENTUM - Plans and Targets API
// =============================================================================

export const royalMomentumPlansAndTargetsApi = createApi({
    reducerPath: "royalMomentumPlansAndTargetsApi",
    baseQuery: apiRequestBaseQuery,
    refetchOnMountOrArgChange: true,
    tagTypes: ["PlansAndTargets"],
    endpoints: (builder) => ({
        // =====================================================================
        // PERFORMANCE VS TARGET
        // =====================================================================

        // Total Revenue (Commercial Snapshot "Total Revenue YTD" card) — V2.
        // Shares the GET_TOTAL_REVENUE_WITH_TARGET_V2 endpoint; YTD is expressed as
        // year (from params.startDate) + quarter:null. `agency_id` = params.travelAgencyUid
        // (= selectedBrand.uid = agency_id_no_r). Reads `total_revenue` from the response.
        getProTotalRevenue: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getTotalRevenueWithTargetV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.travelAgencyUid || null,
                        region: params.region === "all" ? null : params.region || null,
                        year: params.year || params.startDate || null,
                        quarter: params.quarter ?? null,
                    },
                };
            },
            providesTags: ["PlansAndTargets"],
        }),

        // Total Passengers (Commercial Snapshot "Passengers YTD" card) — V2. Shares the
        // GET_TOTAL_PASSENGERS_WITH_TARGET_V2 endpoint; YTD is expressed as year (from
        // params.startDate) + quarter:null. `agency_id` = params.travelAgencyUid (= selectedBrand.uid
        // = agency_id_no_r). Reads `total_passengers` from the response. When
        // `params.comparePreviousPeriod` is set, also sends `compare_previous_period: true`, which
        // makes the response include `yoy_passengers_performance` (already a percentage) — this
        // replaces a second call for last year's total_passengers to compute the YoY delta manually.
        getProTotalPassengers: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getTotalPassengersWithTargetV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.travelAgencyUid || null,
                        region: params.region === "all" ? null : params.region || null,
                        year: params.year || params.startDate || null,
                        quarter: params.quarter ?? null,
                        ...(params.comparePreviousPeriod ? { compare_previous_period: true } : {}),
                    },
                };
            },
            providesTags: ["PlansAndTargets"],
        }),

        // Override Tiers — V2 (`agency_id` replaces `travel_agency_uid`).
        getProOverrideTiers: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getCommercialTargetV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.travelAgencyUid || null,
                        year: params.year,
                        quarter: params.quarter,
                    },
                };
            },
            providesTags: ["PlansAndTargets"],
        }),

        // Total Revenue YOY Performance — V2.
        // `agency_id` replaces `travel_agency_uid`; value is params.travelAgencyUid
        // (= selectedBrand.uid = agency_id_no_r).
        getProTotalRevenueYoyPerformance: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getTotalRevenueYoyPerformanceV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.travelAgencyUid || null,
                        region: params.region === "all" ? null : params.region || null,
                        type: params.type || "year",
                        start_date: params.startDate,
                    },
                };
            },
            providesTags: ["PlansAndTargets"],
        }),

        // Total Revenue with Target (Performance vs Target card) — V2.
        // `agency_id` replaces `travel_agency_uid`; value is params.travelAgencyUid
        // (= selectedBrand.uid = agency_id_no_r).
        getProTotalRevenueWithTarget: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getTotalRevenueWithTargetV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.travelAgencyUid || null,
                        region: params.region === "all" ? null : params.region || null,
                        year: params.year,
                        quarter: params.quarter,
                    },
                };
            },
            providesTags: ["PlansAndTargets"],
        }),

        // Total Passengers with Target (Performance vs Target card) — V2.
        // `agency_id` replaces `travel_agency_uid`; value is params.travelAgencyUid
        // (= selectedBrand.uid = agency_id_no_r).
        getProTotalPassengersWithTarget: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getTotalPassengersWithTargetV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.travelAgencyUid || null,
                        region: params.region === "all" ? null : params.region || null,
                        year: params.year,
                        quarter: params.quarter,
                    },
                };
            },
            providesTags: ["PlansAndTargets"],
        }),

        // =====================================================================
        // PLANS & GOALS
        // =====================================================================

        // Get Plans — V2 (`agency_id` replaces `travel_agency_uid`).
        // params: { year, travelAgencyUid? }
        getProPlans: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getPlansV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.travelAgencyUid || null,
                        year: params.year,
                    },
                };
            },
            providesTags: ["PlansAndTargets"],
        }),

        // Current Goals — the "Current Goals" / Progress Tracker tab.
        //
        // Scoped to one category at a time, so the page calls this once per entry in
        // PLAN_GOAL_CATEGORIES. Returns `current_value`, `target` and a server-computed
        // `target_percentage` per goal, which is why the tracker no longer derives progress from
        // GET_PLANS. GET_PLANS is still needed by the Edit Goals tab.
        // params: { year, categoryPlan, travelAgencyUid? }
        getProCurrentGoals: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getCurrentGoals,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.travelAgencyUid || null,
                        year: params.year,
                        category_plan: params.categoryPlan,
                    },
                };
            },
            providesTags: ["PlansAndTargets"],
        }),

        // Save Plan Target — V2 (`agency_id` replaces `travel_agency_uid`).
        // params: { items, travelAgencyUid? }
        saveProPlanTarget: builder.mutation({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.savePlanTargetV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.travelAgencyUid || null,
                        items: params.items,
                    },
                };
            },
            invalidatesTags: ["PlansAndTargets"],
        }),

        // Delete Plan Target — V2 (`agency_id` replaces `travel_agency_uid`).
        // params: { itemTargetUid, travelAgencyUid? }
        deleteProPlanTarget: builder.mutation({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.deletePlanTargetV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.travelAgencyUid || null,
                        item_target_uid: params.itemTargetUid,
                    },
                };
            },
            invalidatesTags: ["PlansAndTargets"],
        }),
    }),
});

// Export hooks for usage in components
export const {
    // Performance vs Target
    useGetProTotalRevenueQuery,
    useGetProTotalPassengersQuery,
    useGetProOverrideTiersQuery,
    useGetProTotalRevenueYoyPerformanceQuery,
    useGetProTotalRevenueWithTargetQuery,
    useGetProTotalPassengersWithTargetQuery,
    // Plans & Goals
    useGetProPlansQuery,
    useGetProCurrentGoalsQuery,
    useSaveProPlanTargetMutation,
    useDeleteProPlanTargetMutation,
} = royalMomentumPlansAndTargetsApi;
